import { faCalendarCheck, faDownload, faPlus, faPrint, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import Page, { PageActionsType } from "components/common/page/Page";
import { AppRoutes, URI_PARAMETER_KEYS } from "config/router";
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import BookingDatalist, { BookingDatalistPropsType } from "components/entities/accommodation/booking/BookingDatalist";
import { uuid } from "config/app-constants";
import { Conditions, DatalistFilterDefinitions } from "utilities/datalist/datalist-filter-utilities";
import OpenByIdModal from "../../../components/common/page/entity-page-views/OpenByIdModal";
import ModalPortal from "../../../components/modal/ModalPortal";
import { DatalistDataSourceFilterType } from "types/datalist-types";
import { graph } from "config/axios-context";
import { useParams } from "react-router-dom";

const OPEN_BY_ID_MODAL = "open-buy-id";

const BookingDatalistPage = () => {
    const { [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId } = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const context = useAppContext();
    const pageActions: PageActionsType = [
        {
            icon: faUpRightFromSquare,
            link: () => {
                context.modal.open(OPEN_BY_ID_MODAL);
            },
            title: context.t('button.open'),
            name: 'open'
        },
        {
            icon: faPrint,
            link: () => {
                const url = targetSeasonId ? AppRoutes.PRINT_ACCOMMODATION_BOOKING_SEASON : AppRoutes.PRINT_ACCOMMODATION_BOOKING;
                context.navigate(url, { targetSeasonId: targetSeasonId || '' });
            },
            title: "Print",
            name: 'print'
        },
        {
            icon: faPlus,
            link: AppRoutes.ACCOMMODATION_BOOKING_FORM,
            hide: !context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE) || targetSeasonId ? true : false,
            name: 'add',
            title: context.t('button.add')
        },
        {
            icon: faDownload,
            link: () => {
                window.location = "/api/AccommodationPrint/ExportBooking" as any;
            },
            title: "Download",
            name: 'download'
        },
        {
            icon: faCalendarCheck,
            link: AppRoutes.ACCOMMODATION_OVERVIEW_AVAILABILITY,
            title: context.t('pages.accommodation.availability'),
            name: 'availability',
            hide: targetSeasonId ? true : false
        }
    ]
    const openByIdDesc = (
        <p className="pt-3 whitespace-pre text-gray-500">
            <hr />
            Can open
            <br />
            - numbers open booking by id
            <br />
            - <b>s</b>[in seasin id] prefix open booking with s.id
            <br />
            - <b>r</b>[room name] prefix open booking that that day is on that room
        </p>
    );

    let filter: DatalistDataSourceFilterType | undefined = undefined;
    if (!targetSeasonId) {
        filter = {
            id: uuid(),
            logic: 'and',
            filters: [
                DatalistFilterDefinitions.seasonFilterDefinition(context),
                {
                    id: uuid(),
                    name: 'isCancelled',
                    type: 'boolean',
                    condition: Conditions.eq,
                    value: false
                },
                {
                    id: uuid(),
                    name: 'status',
                    type: 'reference',
                    condition: Conditions.eq,
                    value: 'staingAndFuture'
                }
            ]
        }
    }

    const bookingDatalistConfig: BookingDatalistPropsType = {
        name: "pages-accommodation-booking-list",
        hasBottomNavigation: true,
        config: {
            dataSource: {
                filter
            },
            onItemClick: {
                type: 'doubleClick',
                onClick: (item) => {
                    context.navigate(AppRoutes.ACCOMMODATION_BOOKING_VIEW, { id: item.id });
                }
            }
        },
        fixedFilters: !targetSeasonId ? undefined : {
            seasonId: {
                eq: Number(targetSeasonId)
            }
        }
    }

    const openBookingById = async (ref: string) => {
        let id: string = '0';
        if (/^[0-9]{1,}$/.test(ref)) {
            id = ref;
        } else {
            var response = await graph({
                query: `
                    query BookingView($ref: String) {
                        booking(ref: $ref) {
                            id
                        }
                    }
                `,
                variables: {
                    ref: ref
                }
            });

            id = response.data.booking?.id;
        }

        context.navigate(AppRoutes.ACCOMMODATION_BOOKING_VIEW, { id: id }, {
            replace: true
        });
    }

    return (
        <>
            <Page title="pages.accommodation.booking.list" wFull actions={pageActions}>
                <BookingDatalist {...bookingDatalistConfig} />
            </Page>
            <ModalPortal name={OPEN_BY_ID_MODAL} title="Open by id">
                <OpenByIdModal onOpen={c => openBookingById(c?.toString())} description={openByIdDesc} />
            </ModalPortal>
        </>
    );
};

export default BookingDatalistPage;
